import { create } from 'zustand';

import type { OAuthProvider } from '@/lib/api/auth';

import type { OnboardingStep } from './hooks/useOnboardingFlow';

/**
 * A social login that is waiting for an account to belong to. Onboarding
 * launched with one creates the account for the connected owner EOA and
 * associates this login with it in the same run, instead of leaving the user
 * with a login nothing is bound to.
 *
 * In memory only: on a reload the still-unbound OAuth cookie re-opens the
 * binding dialog, where the user picks a route again.
 */
export interface SocialAssociationIntent {
    provider: OAuthProvider;
    accountHash: string;
}

interface OnboardingLauncherState {
    /** Step requested by an external surface (e.g. the deposit panel). */
    requestedStep: OnboardingStep | null;
    /**
     * The onboarding dialog is on screen. `useOnboardingFlow` owns the open
     * state and publishes it here, because onboarding deliberately asks the
     * user to connect and switch wallets: global watchers that react to a
     * wallet switch have to stay quiet for as long as this is true.
     */
    dialogOpen: boolean;
    setDialogOpen: (open: boolean) => void;
    /**
     * Temporary launch gate: this user still needs a Snappy Wallet and the
     * voucher requirement is on, so surfaces outside onboarding must not promise
     * that a first deposit creates the wallet on its own. Remove with the
     * voucher system after launch.
     */
    admissionRequired: boolean;
    setAdmissionRequired: (required: boolean) => void;
    /**
     * Onboarding was launched from a trading-window attempt that dead-ended on a
     * missing Snappy account, so finishing it must drop the user back into the
     * ceremony they were interrupted in.
     */
    resumeReauthOnFinish: boolean;
    setResumeReauthOnFinish: (resume: boolean) => void;
    /**
     * Onboarding was launched as account creation for a brand new user, whose
     * only viable path is a connected wallet (the account is created by owner
     * signatures). The wallet step then drops its social alternative.
     */
    walletFirst: boolean;
    setWalletFirst: (walletFirst: boolean) => void;
    /**
     * Onboarding is running for a social login with no account yet: the run
     * provisions the account for the connected owner and carries this login into
     * it. Null for every other launch.
     */
    associateSocial: SocialAssociationIntent | null;
    setAssociateSocial: (intent: SocialAssociationIntent | null) => void;
    /**
     * Open the onboarding dialog at a step, even if already completed.
     *
     * `associateSocial` is sticky: it is only replaced when the option is
     * explicitly passed, so reopening a minimized run does not lose it.
     */
    launch: (
        step?: OnboardingStep,
        options?: {
            resumeReauthOnFinish?: boolean;
            walletFirst?: boolean;
            associateSocial?: SocialAssociationIntent | null;
        },
    ) => void;
    clear: () => void;
}

/**
 * Lets surfaces outside the onboarding feature (deposit panel, banners) pop
 * the onboarding dialog at a specific step — e.g. re-running the Snappy
 * Wallet setup after the first-login flow was already completed.
 */
export const useOnboardingLauncher = create<OnboardingLauncherState>((set) => ({
    requestedStep: null,
    dialogOpen: false,
    setDialogOpen: (open) =>
        set((current) =>
            current.dialogOpen === open ? current : { dialogOpen: open },
        ),
    admissionRequired: false,
    setAdmissionRequired: (required) =>
        set((current) =>
            current.admissionRequired === required
                ? current
                : { admissionRequired: required },
        ),
    resumeReauthOnFinish: false,
    setResumeReauthOnFinish: (resume) => set({ resumeReauthOnFinish: resume }),
    walletFirst: false,
    setWalletFirst: (walletFirst) => set({ walletFirst }),
    associateSocial: null,
    setAssociateSocial: (intent) => set({ associateSocial: intent }),
    launch: (step = 'wallet', options) =>
        set((current) => ({
            requestedStep: step,
            resumeReauthOnFinish: options?.resumeReauthOnFinish ?? false,
            walletFirst: options?.walletFirst ?? false,
            // NOT `?? null`: the surfaces that reopen a minimized run (the
            // queue pill, the trading banner, the deposit panel) all launch
            // without options, and dropping the intent there would silently
            // turn an association run into a plain social one, whose wallet is
            // created without ever registering the login. Only an explicit
            // value, null included, replaces it; `finish`/`decline` clear it.
            associateSocial:
                options && 'associateSocial' in options
                    ? (options.associateSocial ?? null)
                    : current.associateSocial,
        })),
    clear: () => set({ requestedStep: null }),
}));
